import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import EditProductForm from "../../components/form/products/EditProductForm";
import ProductLinksTable from "../../components/tables/Products/ProductOfferTable";
import CheckoutOfferTable from "../../components/tables/CheckoutBuilder/CheckoutBuilderTable";
import CoProducerTable from "../../components/co-producer/CoProducerTable";
import PixelTable from "../../components/pixel/PixelTable";
import BumpTable from "../../components/bump/BumpTable";
import api from "../../services/api";
import authService from "../../services/authService";
import CouponTable from "../../components/coupon/CouponTable";
import UpsellTable from "../../components/upsell/UpsellTable";
import AffiliatesTable from "../../components/affiliates/AffiliatesTable";
import EbookDeliveryTab from "../../components/ebook/EbookDeliveryTab";

export default function EditProduct() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [productName, setProductName] = useState<string>("");
    const [productType, setProductType] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);

    // Definindo um tipo para as abas e mapeamento para os parâmetros da URL
    type TabType = "geral" | "ofertas" | "pixel" | "bump" | "coupon" | "upsell" | "checkout" | "co-producao" | "afiliados" | "entrega";

    // Mapeamento entre os valores da URL e os tipos de abas
    const tabMapping: Record<string, TabType> = {
        "general": "geral",
        "ofertas": "ofertas",
        "pixel": "pixel",
        "bump": "bump",
        "coupon": "coupon",
        "upsell": "upsell",
        "checkout": "checkout",
        "coproduction": "co-producao",
        "affiliates": "afiliados",
        "delivery": "entrega"
    };

    // Mapeamento inverso para usar ao atualizar a URL
    const reverseTabMapping: Record<TabType, string> = {
        "geral": "general",
        "ofertas": "ofertas",
        "pixel": "pixel",
        "bump": "bump",
        "coupon": "coupon",
        "upsell": "upsell",
        "checkout": "checkout",
        "co-producao": "coproduction",
        "afiliados": "affiliates",
        "entrega": "delivery"
    };

    // Função para obter a aba ativa a partir dos parâmetros da URL
    const getActiveTabFromUrl = (): TabType => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');

        if (tabParam && Object.keys(tabMapping).includes(tabParam)) {
            return tabMapping[tabParam];
        }

        return "geral"; // Aba padrão se não houver parâmetro ou se for inválido
    };

    // Estado para controlar a aba ativa
    const [activeTab, setActiveTab] = useState<TabType>(getActiveTabFromUrl());

    // Buscar o nome do produto quando o componente carregar
    useEffect(() => {
        const fetchProductName = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const response = await api.get(`/api/products/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.product) {
                    setProductName(response.data.product.name);
                    setProductType(response.data.product.type || "");
                }
            } catch (error) {
                console.error("Erro ao buscar nome do produto:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProductName();
    }, [id]);

    // Atualizar a aba ativa quando a URL mudar
    useEffect(() => {
        setActiveTab(getActiveTabFromUrl());
    }, [location.search]);

    // Buscar dados do produto da API
    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            try {
                // Aqui você deve fazer a chamada real para sua API
                const response = await fetch(`/api/products/${id}`);
                if (!response.ok) {
                    console.error("Erro ao buscar produto:", response.statusText);
                }
                // Não precisamos armazenar os dados do produto aqui, pois os componentes
                // filhos farão suas próprias chamadas API com o productId
            } catch (error) {
                console.error("Erro ao buscar produto:", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProduct();
        } else {
            setLoading(false);
        }
    }, [id]);

    // Função para mudar de aba com tipagem correta
    const handleTabChange = (tabId: TabType) => {
        setActiveTab(tabId);

        // Atualizar a URL com o parâmetro de consulta
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('tab', reverseTabMapping[tabId]);

        // Atualizar a URL sem recarregar a página
        navigate(`/products/edit/${id}?${searchParams.toString()}`, { replace: true });
    };

    // Função para voltar à página anterior
    const handleGoBack = () => {
        navigate("/products");
    };

    return (
        <>
            <PageMeta
                title={`Sellyo - Editar Produto: ${productName}`}
                description="Editar configurações do produto"
            />
            <PageBreadcrumb
                pageTitle="Editar produto"
                pageDescription={`Editando produto: ${productName}`}
                showBackButton={true}
                onBack={handleGoBack}
            />

            {/* Sistema de abas */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-800">
                <ul className="flex flex-wrap -mb-px">
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("geral")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "geral"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Geral
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("ofertas")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "ofertas"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Ofertas
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("pixel")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "pixel"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Pixel
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("bump")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "bump"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Order Bump
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("coupon")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "coupon"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Cupom
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("upsell")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "upsell"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Upsell
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("checkout")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "checkout"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Checkout
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("co-producao")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "co-producao"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Co-produção
                        </button>
                    </li>
                    <li className="mr-2">
                        <button
                            onClick={() => handleTabChange("afiliados")}
                            className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "afiliados"
                                ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            Afiliados
                        </button>
                    </li>
                    {/* Entrega por e-mail: só para produto do tipo e-book */}
                    {productType === "ebook" && (
                        <li className="mr-2">
                            <button
                                onClick={() => handleTabChange("entrega")}
                                className={`inline-block py-4 px-4 text-sm font-medium ${activeTab === "entrega"
                                    ? "text-blue-600 border-b-2 border-blue-600 dark:text-blue-500 dark:border-blue-500"
                                    : "text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                    }`}
                            >
                                Entrega
                            </button>
                        </li>
                    )}
                </ul>
            </div>

            {/* Conteúdo das abas */}
            <div className="grid grid-cols-12 gap-4 md:gap-6">
                <div className="col-span-12">
                    {/* Conteúdo da aba Geral */}
                    {activeTab === "geral" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <EditProductForm
                                    productId={id}
                                    onSuccess={(data) => {
                                        // Se for exclusão, redirecionar para a lista
                                        if (data && data.message && data.message.includes("excluído")) {
                                            navigate("/products");
                                        }
                                        // Caso contrário, é uma atualização bem-sucedida
                                        // Aqui você pode implementar sua própria lógica de tratamento de sucesso
                                    }}
                                    onError={(error) => {
                                        console.error("Erro ao processar produto:", error);
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Ofertas */}
                    {activeTab === "ofertas" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <ProductLinksTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Pixel */}
                    {activeTab === "pixel" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <PixelTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Bump */}
                    {activeTab === "bump" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <BumpTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Coupon */}
                    {activeTab === "coupon" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <CouponTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Upsell */}
                    {activeTab === "upsell" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <UpsellTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Checkout */}
                    {activeTab === "checkout" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <CheckoutOfferTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Co-produção */}
                    {activeTab === "co-producao" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <CoProducerTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Afiliados */}
                    {activeTab === "afiliados" && (
                        <div className="p-6">
                            {loading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <AffiliatesTable
                                    productId={id}
                                />
                            )}
                        </div>
                    )}

                    {/* Conteúdo da aba Entrega (e-book por e-mail) */}
                    {activeTab === "entrega" && productType === "ebook" && id && (
                        <div className="p-6">
                            <EbookDeliveryTab productId={id} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}